import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Shield, BarChart3, Users, Calendar } from 'lucide-react'
import { initiateInstagramConnection } from '@/lib/api'
import { SimpleIcon } from '@adthlete/common'

const InstagramConnect = () => {
  const navigate = useNavigate()
  const [isConnecting, setIsConnecting] = useState(false)
  // The backend sends the athlete back here when this instance can't connect Instagram
  const notAvailable = useSearchParams()[0].get('error') === 'not_available'

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      // Redirect to Instagram to authorize
      await initiateInstagramConnection()
    } catch (error) {
      console.error('Failed to initiate Instagram connection:', error)
      setIsConnecting(false)
    }
  }

  const benefits = [
    {
      icon: <Users className="h-5 w-5 text-[#C0FD72]" />,
      title: 'Campaign Management',
      description: 'Seamlessly manage all your brand collaborations and content submissions',
    },
    {
      icon: <BarChart3 className="h-5 w-5 text-[#C0FD72]" />,
      title: 'Performance Analytics',
      description: 'Track your post performance and engagement metrics in real-time',
    },
    {
      icon: <Calendar className="h-5 w-5 text-[#C0FD72]" />,
      title: 'Content Scheduling',
      description: 'Schedule and plan your sponsored content for optimal engagement',
    },
    {
      icon: <Shield className="h-5 w-5 text-[#C0FD72]" />,
      title: 'Secure Connection',
      description: 'Your data is protected with industry-standard security measures',
    },
  ]

  const permissions = [
    'Read your basic Instagram account information',
    'Access your Instagram media and posts',
    'View your Instagram insights and analytics',
  ]

  return (
    <div className="w-full max-w-4xl mx-auto pb-4">
      <div className="mb-4 md:mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/settings?tab=social')}
          className="text-gray-400"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Settings
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">Connect Instagram</h1>
          <p className="text-gray-400">Link your Instagram business account to get started</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Professional Account Notice */}
        <div className="md:col-span-2 mb-4">
          <Card className="bg-amber-500/10 backdrop-blur-xs border border-amber-500/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="shrink-0 bg-amber-500/20 p-2 rounded-full">
                  <SimpleIcon
                    slug="instagram"
                    color="fbbf24"
                    height={16}
                    width={16}
                    alt="Instagram"
                  />
                </div>
                <div>
                  <h4 className="text-amber-400 font-medium text-sm mb-2">
                    Professional Account Required
                  </h4>
                  <p className="text-gray-300 text-sm mb-3">
                    To connect with our platform, your Instagram account needs to be set up as a{' '}
                    <strong>professional business account</strong>. This allows access to the
                    business features and analytics we need to provide our services.
                  </p>
                  <p className="text-gray-300 text-sm mb-3">
                    If your account isn't already set up as a business account, you'll need to
                    convert it first. As a professional creator, this makes sense for your brand
                    anyway!
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        window.open('https://help.instagram.com/502981923235522', '_blank')
                      }
                      className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10 text-xs"
                    >
                      How to Convert to Business Account
                    </Button>
                    <p className="text-xs text-gray-400 sm:mt-2">
                      Business and Creator accounts both work. You don't need a Facebook page.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Connect Card */}
        <Card variant="dark" className="overflow-hidden">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-linear-to-r from-[#FF3A89] to-[#FFC702] p-4 rounded-full">
                <SimpleIcon
                  slug="instagram"
                  color="ffffff"
                  height={32}
                  width={32}
                  alt="Instagram"
                />
              </div>
            </div>
            <CardTitle className="text-white text-xl">Connect Your Instagram</CardTitle>
            <CardDescription className="text-gray-400">
              Connect your Instagram business account to unlock powerful collaboration tools
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-white font-medium mb-3">What we'll access:</h3>
              <ul className="space-y-2">
                {permissions.map((permission, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                    <div className="w-1.5 h-1.5 bg-[#C0FD72] rounded-full mt-2 shrink-0" />
                    {permission}
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-4 border-t border-white/10">
              <Button
                onClick={handleConnect}
                disabled={isConnecting || notAvailable}
                variant="lime"
                className="w-full font-medium"
                size="lg"
              >
                {isConnecting ? 'Connecting...' : 'Connect Instagram Account'}
              </Button>

              <p
                className={`text-xs text-center mt-3 ${notAvailable ? 'text-amber-400' : 'text-gray-400'}`}
              >
                {notAvailable
                  ? 'Instagram connection is only available on app.athios.ai'
                  : "You'll be redirected to Instagram to authorize the connection"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Benefits Card */}
        <Card variant="dark" className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-white">Why Connect Instagram?</CardTitle>
            <CardDescription className="text-gray-400">
              Unlock these powerful features when you connect your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex gap-3">
                  <div className="shrink-0">{benefit.icon}</div>
                  <div>
                    <h4 className="text-white font-medium text-sm">{benefit.title}</h4>
                    <p className="text-gray-400 text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Security Notice */}
      <Card className="mt-6 bg-white/5 backdrop-blur-xs border border-white/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-[#C0FD72] shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-medium text-sm mb-1">Your Privacy is Protected</h4>
              <p className="text-gray-400 text-sm">
                We only access the minimum permissions required to provide our services. You can
                disconnect your account at any time from your settings page. We never post to your
                account.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default InstagramConnect
